import { BaseResource } from './base';
import {
  Contract,
  CreateContractDto,
  RenewContractDto,
  FreezeContractDto,
  CancelContractDto,
  SuspendContractDto,
  ResumeContractDto,
  ReactivateContractDto,
  GetContractsParams,
  ContractLifecycleEvent,
  CancellationAnalytics,
} from '../models/contracts';
import { RequestOptions, PaginatedResponseDto } from '../types';

export class ContractsResource extends BaseResource {
  private basePath = 'contracts';

  async createContract(data: CreateContractDto, options?: RequestOptions): Promise<Contract> {
    return this.client.post<Contract>(this.basePath, data, options);
  }

  async getGymContracts(
    params?: GetContractsParams,
    options?: RequestOptions,
  ): Promise<PaginatedResponseDto<Contract>> {
    return this.paginate<Contract>(this.basePath, params, options);
  }

  async getContract(id: string, options?: RequestOptions): Promise<Contract> {
    return this.client.get<Contract>(`${this.basePath}/${id}`, undefined, options);
  }

  async getClientContracts(clientId: string, options?: RequestOptions): Promise<Contract[]> {
    return this.client.get<Contract[]>(`${this.basePath}/client/${clientId}`, undefined, options);
  }

  async renewContract(
    id: string,
    data: RenewContractDto,
    options?: RequestOptions,
  ): Promise<Contract> {
    return this.client.post<Contract>(`${this.basePath}/${id}/renew`, data, options);
  }

  async freezeContract(
    id: string,
    data: FreezeContractDto,
    options?: RequestOptions,
  ): Promise<Contract> {
    return this.client.post<Contract>(`${this.basePath}/${id}/freeze`, data, options);
  }

  async cancelContract(
    id: string,
    data: CancelContractDto,
    options?: RequestOptions,
  ): Promise<Contract> {
    return this.client.put<Contract>(`${this.basePath}/${id}/cancel`, data, options);
  }

  async suspendContract(
    id: string,
    data: SuspendContractDto,
    options?: RequestOptions,
  ): Promise<Contract> {
    return this.client.post<Contract>(`${this.basePath}/${id}/suspend`, data, options);
  }

  async resumeContract(
    id: string,
    data: ResumeContractDto,
    options?: RequestOptions,
  ): Promise<Contract> {
    return this.client.post<Contract>(`${this.basePath}/${id}/resume`, data, options);
  }

  async reactivateContract(
    id: string,
    data: ReactivateContractDto,
    options?: RequestOptions,
  ): Promise<Contract> {
    return this.client.post<Contract>(`${this.basePath}/${id}/reactivate`, data, options);
  }

  async getLifecycleHistory(
    id: string,
    options?: RequestOptions,
  ): Promise<ContractLifecycleEvent[]> {
    return this.client.get<ContractLifecycleEvent[]>(
      `${this.basePath}/${id}/lifecycle-history`,
      undefined,
      options,
    );
  }

  async getCancellationAnalytics(
    params?: { startDate?: string; endDate?: string },
    options?: RequestOptions,
  ): Promise<CancellationAnalytics> {
    return this.client.get<CancellationAnalytics>(
      `${this.basePath}/analytics/cancellation-reasons`,
      params,
      options,
    );
  }

  async resendWhatsAppNotification(
    contractId: string,
    options?: RequestOptions,
  ): Promise<{ success: boolean; message: string }> {
    return this.client.post<{ success: boolean; message: string }>(
      `${this.basePath}/${contractId}/resend-whatsapp`,
      {},
      options,
    );
  }
}
